import {
  Box,
  Button,
  Card,
  Flex,
  Group,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import { Filter, Search } from 'lucide-react';
import type {
  PatientListSort,
  PatientMeasurementsFilter,
} from '../utils/filterPatientsList';

interface PatientsHubFiltersProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  measurementsFilter: PatientMeasurementsFilter;
  onMeasurementsFilterChange: (value: PatientMeasurementsFilter) => void;
  sortBy: PatientListSort;
  onSortByChange: (value: PatientListSort) => void;
  filteredCount: number;
  totalCount: number;
  onReset: () => void;
  showReset: boolean;
}

const measurementsFilterOptions = [
  { value: 'all', label: 'Tutte le rilevazioni' },
  { value: 'with', label: 'Con rilevazioni corporee' },
  { value: 'without', label: 'Senza rilevazioni corporee' },
] as const;

const sortOptions = [
  { value: 'code-asc', label: 'Codice A → Z' },
  { value: 'code-desc', label: 'Codice Z → A' },
  { value: 'id-asc', label: 'ID crescente' },
  { value: 'id-desc', label: 'ID decrescente' },
  { value: 'updated-desc', label: 'Aggiornati di recente' },
] as const;

export function PatientsHubFilters({
  searchValue,
  onSearchChange,
  measurementsFilter,
  onMeasurementsFilterChange,
  sortBy,
  onSortByChange,
  filteredCount,
  totalCount,
  onReset,
  showReset,
}: PatientsHubFiltersProps) {
  return (
    <Card shadow='sm' withBorder radius='sm' p='lg'>
      <Flex direction='column' gap='md'>
        <Flex justify='space-between' align='center' wrap='wrap' gap='md'>
          <Group gap='xs'>
            <Filter size={16} color='var(--mantine-color-gray-5)' />
            <Text size='sm' fw={500}>
              Cerca e filtra utenti
            </Text>
          </Group>
          <Text size='sm' c='dimmed'>
            {filteredCount === totalCount
              ? `${totalCount} utenti`
              : `${filteredCount} di ${totalCount} utenti`}
          </Text>
        </Flex>

        <Flex
          align='center'
          wrap='wrap'
          gap='md'
          direction={{ base: 'column', sm: 'row' }}
        >
          <Box flex={1} miw={280} w={{ base: '100%', sm: 'auto' }}>
            <TextInput
              placeholder='Cerca per codice o ID utente...'
              value={searchValue}
              onChange={event => onSearchChange(event.currentTarget.value)}
              leftSection={
                <Search size={16} color='var(--mantine-color-gray-6)' />
              }
              radius='sm'
              size='sm'
            />
          </Box>

          <Group gap='xs' wrap='wrap' w={{ base: '100%', sm: 'auto' }}>
            <Select
              aria-label='Filtra per rilevazioni corporee'
              data={[...measurementsFilterOptions]}
              value={measurementsFilter}
              onChange={value =>
                onMeasurementsFilterChange(
                  (value as PatientMeasurementsFilter) ?? 'all'
                )
              }
              size='sm'
              radius='sm'
              w={{ base: '100%', xs: 240 }}
            />
            <Select
              aria-label='Ordina utenti'
              data={[...sortOptions]}
              value={sortBy}
              onChange={value =>
                onSortByChange((value as PatientListSort) ?? 'code-asc')
              }
              size='sm'
              radius='sm'
              w={{ base: '100%', xs: 220 }}
            />
            {showReset ? (
              <Button variant='subtle' size='sm' onClick={onReset}>
                Reimposta filtri
              </Button>
            ) : null}
          </Group>
        </Flex>
      </Flex>
    </Card>
  );
}
